import { useTranslation } from "@privacy/translations";
import { Stack } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { RegistrationProcessorIcon1, RegistrationControllerIcon1 } from "../../../shared/icons";
import { usePermission } from "../../../shared/permissions";
import { useGetSystemById, useGetSystemForm, useUpdateSystemAnswers } from "../../../api/systems";
import { classificationTemplateQueryOptions } from "../../../api/classificationTemplate";
import { DynamicForm } from "../../../dynamicForm";
import { usePrivacyNavigate } from "../../../shared/usePrivacyNavigate";
import { Drawer } from "../../../shared/Drawer";
import { DynamicGrid } from "../../../dynamicGrid";
import { SystemClassificationSection } from "./SystemClassificationSection";
import { SystemConnections } from "./SystemConnections";
import { SystemsDrawerHeader } from "./SystemsDrawerHeader";
import { OldDpaLinksField } from "./suppliersAgreements/OldDpaLinksField";
import { withAgreementsOfRemovedSuppliersDropped } from "./suppliersAgreements/withAgreementsOfRemovedSuppliersDropped";
import { OLD_DPA_LINK_QUESTION_ID } from "./suppliersAgreements/constants";
import { useCurrentOrganisation } from "../../../root/CurrentOrganisationProvider";

export const SystemInlineFormDrawer = () => {
  const systemId = DynamicGrid.useSelectedRowId();
  const { t } = useTranslation();
  const { classificationEnabled } = useCurrentOrganisation();
  const { hasPermission } = usePermission();
  const { mutateAsync: updateSystemAnswers, isError: isSaveError, isPending } = useUpdateSystemAnswers();
  const { navigate, paths } = usePrivacyNavigate();
  const systemFormQuery = useGetSystemForm();
  const systemQuery = useGetSystemById(systemId);

  const templateQuery = useQuery({
    ...classificationTemplateQueryOptions(),
    enabled: classificationEnabled,
  });

  return (
    <DynamicGrid.Drawer>
      {(() => {
        if (!systemQuery.data || !systemFormQuery.data) return <Drawer.Loading />;
        if (classificationEnabled && !templateQuery.data) return <Drawer.Loading />;

        const system = systemQuery.data;
        const canEdit = hasPermission("nova-forms-api:system:update") && !system.archivedAt;
        return (
          <DynamicForm.Provider
            onSave={answer =>
              updateSystemAnswers({
                systemId: system.id,
                answers: withAgreementsOfRemovedSuppliersDropped(answer, system.answers),
              })
            }
            fileOwner={{ type: "System", id: system.id }}
            isSaving={isPending}
            isReadOnly={!canEdit}
            isSaveError={isSaveError}
            key={system.id}
            answers={system.answers}
          >
            {({ formState }) => (
              <>
                <SystemsDrawerHeader
                  activeTab="overview"
                  system={system}
                  statusIndicator={DynamicForm.formStateToDrawerHeaderStatus(formState)}
                />
                <Drawer.Content>
                  <DynamicForm.InlineForm
                    id={system.id}
                    form={systemFormQuery.data}
                    renderSlots={{
                      replaceQuestion: questionCard => {
                        if (questionCard.id === OLD_DPA_LINK_QUESTION_ID) {
                          return <OldDpaLinksField questionCard={questionCard} answers={system.answers} />;
                        }
                        return undefined;
                      },
                    }}
                  />
                  {classificationEnabled && templateQuery.data && system.classification && (
                    <Stack sx={{ mt: 6 }}>
                      <SystemClassificationSection
                        systemId={system.id}
                        classification={system.classification}
                        template={templateQuery.data.template}
                        customerCustomizations={templateQuery.data.customerCustomizations}
                        canEdit={canEdit}
                      />
                    </Stack>
                  )}
                  {classificationEnabled && (
                    <Stack sx={{ gap: 3, mt: 6 }}>
                      <DynamicForm.InlineFormSection
                        title={t("System.Connection.StoredIn.Title")}
                        subtitle={t("System.Connection.StoredIn.Subtitle")}
                      >
                        <SystemConnections
                          systemId={system.id}
                          connectionType="stored"
                          connections={system.connectedInformationTypesInfo.storedConnections}
                          canEdit={canEdit}
                        />
                      </DynamicForm.InlineFormSection>
                      <DynamicForm.InlineFormSection
                        title={t("System.Connection.UsedIn.Title")}
                        subtitle={t("System.Connection.UsedIn.Subtitle")}
                      >
                        <SystemConnections
                          systemId={system.id}
                          connectionType="used"
                          connections={system.connectedInformationTypesInfo.usedConnections}
                          canEdit={canEdit}
                        />
                      </DynamicForm.InlineFormSection>
                    </Stack>
                  )}
                  <Stack sx={{ mt: 6 }}>
                    <Drawer.GoToSubDrawerButtonsWrapper>
                      <Drawer.GoToSubDrawerButton
                        Icon={RegistrationControllerIcon1}
                        count={system.connectedControllerRegistrations.totalCount}
                        label={t("SystemsDrawer.SubDrawer.ConnectedRegistrationsController")}
                        onClick={() => navigate(paths.systemsConnectedRegistrations(system.id))}
                      />
                      <Drawer.GoToSubDrawerButton
                        Icon={RegistrationProcessorIcon1}
                        count={system.connectedProcessorRegistrations.totalCount}
                        label={t("SystemsDrawer.SubDrawer.ConnectedRegistrationsProcessor")}
                        onClick={() => navigate(paths.systemsConnectedProcessorRegistrations(system.id))}
                      />
                    </Drawer.GoToSubDrawerButtonsWrapper>
                  </Stack>
                </Drawer.Content>
              </>
            )}
          </DynamicForm.Provider>
        );
      })()}
    </DynamicGrid.Drawer>
  );
};
